import { useState } from "react";
import axios from "axios";
import { Phone, Mail, MessageCircle, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { PHONE, PHONE_TEL, EMAIL, WHATSAPP } from "@/lib/site";
import { dropBurstFromElement } from "@/lib/brandFx";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Büroreinigung",
  "Treppenhausreinigung",
  "Umzugsreinigung",
  "Grundreinigung",
  "Wohnungsreinigung",
  "Hausreinigung",
];

const EMPTY = { name: "", email: "", phone: "", service: "", message: "" };

const WHATSAPP_TEXT =
  "Hallo, ich interessiere mich für Ihre Reinigungsdienstleistungen und möchte gerne ein unverbindliches Angebot erhalten.";
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(
  WHATSAPP_TEXT
)}`;

const EMAIL_SUBJECT = "Anfrage für Reinigungsdienstleistungen";
const EMAIL_BODY = `Hallo,
ich interessiere mich für Ihre Reinigungsdienstleistungen und möchte gerne ein unverbindliches Angebot erhalten.

Gewünschte Leistung:
Ort:
Bevorzugter Termin:

Mit freundlichen Grüßen`;
const EMAIL_LINK = `mailto:${EMAIL}?subject=${encodeURIComponent(
  EMAIL_SUBJECT
)}&body=${encodeURIComponent(EMAIL_BODY)}`;

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service || !form.message) {
      toast.error("Bitte füllen Sie alle Felder aus.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Vielen Dank! Ihre Anfrage wurde gesendet.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-16">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#0077E6] mb-4">
              Kontakt
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-[#0A192F]">
              Jetzt unverbindlich anfragen
            </h2>
            <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed max-w-lg">
              Kontaktieren Sie uns telefonisch, per E-Mail oder WhatsApp – oder
              füllen Sie einfach das Formular aus. Wir melden uns zeitnah bei
              Ihnen zurück.
            </p>

            <div className="mt-10 space-y-4 max-w-md">
              <a
                href={`tel:${PHONE_TEL}`}
                data-testid="contact-phone-button"
                className="flex items-center gap-4 rounded-2xl border border-slate-100 bg-[#F8FAFC] p-5 hover:border-[#0077E6]/40 hover:shadow-md transition-all"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#0077E6] text-white">
                  <Phone className="h-6 w-6" />
                </span>
                <span>
                  <span className="block text-sm text-slate-500">Anrufen</span>
                  <span className="block font-semibold text-[#0A192F]">{PHONE}</span>
                </span>
              </a>

              <a
                href={EMAIL_LINK}
                data-testid="contact-email-button"
                onClick={(e) => dropBurstFromElement(e.currentTarget)}
                className="flex items-center gap-4 rounded-2xl border border-slate-100 bg-[#F8FAFC] p-5 hover:border-[#0077E6]/40 hover:shadow-md transition-all"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#0077E6] text-white">
                  <Mail className="h-6 w-6" />
                </span>
                <span>
                  <span className="block text-sm text-slate-500">E-Mail</span>
                  <span className="block font-semibold text-[#0A192F] break-all">
                    {EMAIL}
                  </span>
                </span>
              </a>

              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-button"
                onClick={(e) => dropBurstFromElement(e.currentTarget)}
                className="flex items-center gap-4 rounded-2xl border border-slate-100 bg-[#F8FAFC] p-5 hover:border-[#25D366]/50 hover:shadow-md transition-all"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#25D366] text-white">
                  <MessageCircle className="h-6 w-6" />
                </span>
                <span>
                  <span className="block text-sm text-slate-500">WhatsApp</span>
                  <span className="block font-semibold text-[#0A192F]">
                    Nachricht senden
                  </span>
                </span>
              </a>
            </div>
          </div>

          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="bg-white p-8 md:p-10 rounded-2xl shadow-[0_20px_50px_rgb(10,25,47,0.08)] border border-slate-100"
          >
            <div className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  data-testid="form-name"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="Ihr Name"
                  className="h-12 rounded-xl"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="email">E-Mail</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="form-email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="ihre@email.de"
                    className="h-12 rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefon</Label>
                  <Input
                    id="phone"
                    data-testid="form-phone"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="Ihre Telefonnummer"
                    className="h-12 rounded-xl"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Gewünschte Leistung</Label>
                <Select
                  value={form.service}
                  onValueChange={(v) => update("service", v)}
                >
                  <SelectTrigger data-testid="form-service" className="h-12 rounded-xl">
                    <SelectValue placeholder="Leistung auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`service-option-${s}`}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Nachricht</Label>
                <Textarea
                  id="message"
                  data-testid="form-message"
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  placeholder="Wie können wir Ihnen helfen?"
                  rows={4}
                  className="rounded-xl resize-none"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="form-submit"
                className="w-full h-13 py-4 rounded-full bg-[#0077E6] text-white hover:bg-[#005bb5] transition-transform hover:scale-[1.02] active:scale-95"
              >
                {loading ? "Wird gesendet..." : "Anfrage senden"}
                {!loading && <Send className="ml-2 h-5 w-5" />}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};
